import { AccountRecord } from '@/shared/account';
import type { ContentReview } from '@/shared/content-review';
import { AdvisorProfileRecord } from '@/shared/advisor-profile';
import { RenewalPlan } from '@/shared/renewal';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { useRef } from 'react';
import { useTenant } from './tenant-context';
import { tenantQueryKey, PUBLIC_REFRESH_MS } from './tenant';
import { z } from 'zod';
import {
  AttemptCreated,
  AttemptResult,
  Brand,
  BrandPut,
  BrandPutResult,
  Card,
  CardList,
  ContentSummary,
  AudioState,
  CredentialList,
  ItemDetail,
  InviteResult,
  LearnerDetail,
  LearnerRow,
  LibraryItem,
  Me,
  Page,
  Progress,
  ProgressPut,
  RegisterRow,
  SessionState,
  hasProgrammeAccess,
  isAccountMode,
  Slot,
  SlotHistoryRow,
  SponsorSettings,
  SponsorSummary,
  TenantAdmin,
  TenantCreate,
  TenantPatch,
  TenantPublic,
  AdminUser,
  VerifyResult,
} from '@/shared/contracts';
import { retainPrivateOwner } from './private-state';
import { uuidv7 } from '@/shared/ids';
import { api, apiEvents, SIGNED_OUT, retryOnce, type CallOptions } from './api';

/**
 * One client for the app. Reads are fresh for half a minute; a 4xx is never retried because the
 * answer will not change; a session read is never cached across sign-in.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: retryOnce,
    },
    mutations: { retry: 0 },
  },
});

/**
 * Person-scoped keys survive losing a programme; everything else is programme data and is dropped
 * the moment access ends (decision 16). `me` is programme data: it carries progress and a credential.
 */
export const PERSON_KEYS = ['session', 'tenant', 'account', 'advisor-profile'];

export const keys = {
  session: ['session'] as const,
  me: ['me'] as const,
  account: ['account'] as const,
  advisorProfile: (userId: string) => ['advisor-profile', userId] as const,
  library: ['library'] as const,
  item: (id: string) => ['item', id] as const,
  attempts: (id: string) => ['attempts', id] as const,
  cards: ['cards'] as const,
  card: (id: string) => ['card', id] as const,
  credentials: ['credentials'] as const,
  renewal: ['renewal-update'] as const,
  verify: (code: string) => ['verify', code] as const,
  sponsor: {
    summary: ['sponsor', 'summary'] as const,
    learners: (q: string, cursor: string | null) => ['sponsor', 'learners', q, cursor] as const,
    learner: (id: string) => ['sponsor', 'learner', id] as const,
    brand: ['sponsor', 'brand'] as const,
    slots: ['sponsor', 'slots'] as const,
    slotHistory: (key: string) => ['sponsor', 'slots', key, 'history'] as const,
    settings: ['sponsor', 'settings'] as const,
  },
  admin: {
    tenants: ['admin', 'tenants'] as const,
    register: (q: string, cursor: string | null) => ['admin', 'register', q, cursor] as const,
    content: ['admin', 'content'] as const,
    users: (q: string) => ['admin', 'users', q] as const,
  },
};

const Empty = z.undefined();
const Saved = z.object({ saved: z.boolean() });
const AuthRequested = z.object({ ok: z.boolean(), link: z.string().optional() });
const AttemptList = z.object({ data: z.array(z.unknown()) });

const qs = (params: Record<string, string | number | null | undefined>): string => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params))
    if (v !== null && v !== undefined && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};

/** All three editors share one reviewed academy revision. */
function refreshTenantConfig(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: keys.sponsor.brand });
  void qc.invalidateQueries({ queryKey: keys.sponsor.settings });
  void qc.invalidateQueries({ queryKey: keys.admin.tenants });
  return qc.invalidateQueries({ queryKey: tenantQueryKey });
}

/** Paint the acknowledged values even if the subsequent public refresh is temporarily unavailable. */
async function acceptTenantBrand(qc: QueryClient, brand: z.infer<typeof Brand>) {
  await qc.cancelQueries({ queryKey: tenantQueryKey });
  qc.setQueryData<TenantPublic>(
    tenantQueryKey,
    (tenant) =>
      tenant && {
        ...tenant,
        name: brand.name,
        short_name: brand.short_name,
        brand_mode: brand.brand_mode,
        accent_light: brand.effective_accent_light,
        accent_dark: brand.effective_accent_dark,
        logo_url: brand.logo_url,
        display_font: brand.display_font,
        signatory_name: brand.signatory_name,
        signatory_title: brand.signatory_title,
      },
  );
  await refreshTenantConfig(qc);
}

/* ---------- reads ---------- */

export const useSession = (enabled = true) => {
  const tenant = useTenant();
  return useQuery({
    queryKey: keys.session,
    enabled,
    queryFn: async ({ signal }) => {
      const session = await api.get('/api/v1/auth/session', SessionState, { signal });
      const allowed = hasProgrammeAccess(session);
      const owner = allowed && session.user ? `${tenant.slug}:${session.user.id}` : null;
      const previous = queryClient.getQueryData<z.infer<typeof SessionState>>(keys.session);
      const switched = !!previous?.user && previous.user.id !== session.user?.id;
      // Losing programme access clears the programme, not the person: with the account layer on,
      // their account and profile stay cached. A different person clears everything regardless.
      const keepers = !switched && isAccountMode(session) ? PERSON_KEYS : ['session', 'tenant'];
      if (!allowed || switched)
        queryClient.removeQueries({
          predicate: (query) => !keepers.includes(String(query.queryKey[0])),
        });
      // Session is read again on every arrival, focus and reconnect, before showing private views.
      retainPrivateOwner(owner);
      return session;
    },
    staleTime: 0,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    refetchInterval: enabled ? 60_000 : false,
    // The same rule as every other read: one dropped request or server failure is tried again
    // before the guard shows "Academy access could not be checked.", which otherwise stays until
    // the next poll, since a server hiccup fires no browser `online` event. A 4xx is final.
    retry: retryOnce,
  });
};
export const useMe = (enabled = true) =>
  useQuery({ queryKey: keys.me, queryFn: () => api.get('/api/v1/me', Me), enabled });

/**
 * The person's own account. It answers on authentication alone, so it is the surface that survives
 * losing a programme; it exists only while the account layer is on, hence the caller-supplied gate.
 */
export const useAccount = (enabled = true) =>
  useQuery({ queryKey: keys.account, queryFn: () => api.get('/api/v1/account', AccountRecord), enabled });
export const useAdvisorProfile = (userId: string, enabled = true) =>
  useQuery({
    queryKey: keys.advisorProfile(userId),
    // Home and Record read the profile for their own cards; they wait until the person is known.
    enabled: enabled && userId.length > 0,
    queryFn: async ({ signal }) => {
      const record = await api.get('/api/v1/me/advisor-profile', AdvisorProfileRecord, { signal });
      if (record.user_id !== userId) throw new Error('Your account changed. Reload your account page.');
      return record;
    },
  });

export const useSaveAdvisorProfile = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (record: AdvisorProfileRecord) => {
      if (qc.getQueryData<z.infer<typeof SessionState>>(keys.session)?.user?.id !== record.user_id)
        throw new Error('Your account changed. Reload your account page.');
      const saved = await api.put('/api/v1/me/advisor-profile', record, AdvisorProfileRecord);
      if (qc.getQueryData<z.infer<typeof SessionState>>(keys.session)?.user?.id !== record.user_id)
        throw new Error('Your account changed. Reload your account page.');
      return saved;
    },
    onSuccess: (_saved, record) => qc.invalidateQueries({ queryKey: keys.advisorProfile(record.user_id) }),
  });
};
export const useLibrary = (enabled = true) =>
  useQuery({
    queryKey: keys.library,
    queryFn: () => api.get('/api/v1/library', z.array(LibraryItem)),
    enabled,
  });
export const useItem = (id: string) =>
  useQuery({
    queryKey: keys.item(id),
    queryFn: () => api.get(`/api/v1/items/${encodeURIComponent(id)}`, ItemDetail),
  });
export const useAttempts = (itemId: string) =>
  useQuery({
    queryKey: keys.attempts(itemId),
    queryFn: () => api.get(`/api/v1/attempts${qs({ item: itemId })}`, AttemptList).then((r) => r.data),
  });
export const useCards = () =>
  useQuery({ queryKey: keys.cards, queryFn: () => api.get('/api/v1/cards', CardList) });
export const useCard = (id: string) =>
  useQuery({
    queryKey: keys.card(id),
    queryFn: () => api.get(`/api/v1/cards/${encodeURIComponent(id)}`, Card),
  });
export const useCredentials = () =>
  useQuery({ queryKey: keys.credentials, queryFn: () => api.get('/api/v1/credentials/me', CredentialList) });
export const useVerify = (code: string) =>
  useQuery({
    queryKey: keys.verify(code),
    queryFn: () => api.get(`/api/v1/verify/${encodeURIComponent(code)}`, VerifyResult),
    enabled: code.length > 0,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    refetchInterval: PUBLIC_REFRESH_MS,
  });

export const useSponsorSummary = () =>
  useQuery({
    queryKey: keys.sponsor.summary,
    queryFn: () => api.get('/api/v1/sponsor/summary', SponsorSummary),
  });
export const useLearners = (q: string, cursor: string | null) =>
  useQuery({
    queryKey: keys.sponsor.learners(q, cursor),
    queryFn: () => api.get(`/api/v1/sponsor/learners${qs({ q, cursor, limit: 50 })}`, Page(LearnerRow)),
  });
export const useLearner = (userId: string) =>
  useQuery({
    queryKey: keys.sponsor.learner(userId),
    queryFn: () => api.get(`/api/v1/sponsor/learners/${encodeURIComponent(userId)}`, LearnerDetail),
  });
export const useBrand = () =>
  useQuery({ queryKey: keys.sponsor.brand, queryFn: () => api.get('/api/v1/sponsor/brand', Brand) });
export const useSlots = () =>
  useQuery({
    queryKey: keys.sponsor.slots,
    queryFn: () => api.get('/api/v1/sponsor/slots', z.object({ data: z.array(Slot) })).then((r) => r.data),
  });
export const useSlotHistory = (key: string) =>
  useQuery({
    queryKey: keys.sponsor.slotHistory(key),
    queryFn: () =>
      api.get(`/api/v1/sponsor/slots/${encodeURIComponent(key)}/history`, z.array(SlotHistoryRow)),
    enabled: key.length > 0,
  });
export const useSponsorSettings = () =>
  useQuery({
    queryKey: keys.sponsor.settings,
    queryFn: () => api.get('/api/v1/sponsor/settings', SponsorSettings),
  });

export const useTenants = () =>
  useQuery({
    queryKey: keys.admin.tenants,
    queryFn: () => api.get('/api/v1/admin/tenants', z.array(TenantAdmin)),
  });
export const useRegister = (q: string, cursor: string | null) =>
  useQuery({
    queryKey: keys.admin.register(q, cursor),
    queryFn: () => api.get(`/api/v1/admin/register${qs({ q, cursor, limit: 50 })}`, Page(RegisterRow)),
  });
export const useContentSummary = () =>
  useQuery({ queryKey: keys.admin.content, queryFn: () => api.get('/api/v1/admin/content', ContentSummary) });
export const useAdminUsers = (q: string) =>
  useQuery({
    queryKey: keys.admin.users(q),
    queryFn: () => api.get(`/api/v1/admin/users${qs({ q })}`, z.array(AdminUser)),
  });

/* ---------- writes ---------- */

/**
 * A mutation whose idempotency key is drawn once per call site and kept until the call succeeds,
 * so a retry after a dropped connection replays the stored answer instead of creating twice.
 */
function useKeyedMutation<TVars, TData>(
  run: (vars: TVars, opts: CallOptions) => Promise<TData>,
  options: Omit<UseMutationOptions<TData, Error, TVars>, 'mutationFn'> = {},
) {
  const key = useRef<string>(uuidv7());
  return useMutation<TData, Error, TVars>({
    ...options,
    mutationFn: (vars) => run(vars, { idempotencyKey: key.current }),
    onSuccess: (data, vars, ctx, mutation) => {
      key.current = uuidv7();
      options.onSuccess?.(data, vars, ctx, mutation);
    },
  });
}

export const useRequestLink = () =>
  useMutation({
    mutationFn: (body: { email: string; next?: string; enrol_code?: string }) =>
      api.post('/api/v1/auth/request', body, AuthRequested),
  });

export const useLogout = () =>
  useMutation({
    mutationFn: () => api.post('/api/v1/auth/logout', undefined, Empty),
    onSuccess: () => {
      apiEvents.dispatchEvent(new Event(SIGNED_OUT));
    },
  });

export const usePatchMe = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string }) => api.patch('/api/v1/me', body, Me),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: keys.me }),
        qc.invalidateQueries({ queryKey: keys.session }),
      ]);
    },
  });
};

/**
 * The photo belongs to the person. With the account layer on the server answers with the account
 * record; with it off, with the `Me` payload exactly as before. Either is accepted, since the switch
 * can change while a page is open, and both cached reads are refreshed.
 */
const PhotoSaved = z.union([Me, AccountRecord]);

export const useProfilePhoto = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, userId }: { file: File | null; userId: string }) => {
      if (qc.getQueryData<z.infer<typeof SessionState>>(keys.session)?.user?.id !== userId)
        throw new Error('Your account changed. Choose the photo again.');
      if (!file) return api.del('/api/v1/me/photo', PhotoSaved, { body: { user_id: userId } });
      const form = new FormData();
      form.set('user_id', userId);
      form.set('file', file);
      return api.upload('/api/v1/me/photo', form, PhotoSaved);
    },
    // Read the current account again: a late response may belong to a previous session or name.
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: keys.me }),
        qc.invalidateQueries({ queryKey: keys.account }),
      ]);
    },
  });
};

/**
 * What the Account page needs, from whichever surface can answer today. With the account layer on
 * that is `/api/v1/account`, which needs no programme; without it, `/api/v1/me`, exactly as before.
 */
export type AccountView = {
  id: string;
  email: string;
  name: string | null;
  photo_url: string | null;
  is_superadmin: boolean;
  /** The optimistic-concurrency token `PATCH /api/v1/account` requires. Null in legacy mode. */
  revision: string | null;
};

export function useAccountView() {
  const session = useSession();
  const signedIn = !!session.data?.user;
  const accounts = isAccountMode(session.data);
  const programme = hasProgrammeAccess(session.data);
  const me = useMe(signedIn && !accounts);
  const account = useAccount(signedIn && accounts);
  const source = accounts ? account : me;
  const record = account.data?.account;
  const legacy = me.data?.user;
  let view: AccountView | null = null;
  if (accounts && record)
    view = {
      id: record.user_id,
      email: record.email,
      name: record.name,
      photo_url: record.photo_url,
      is_superadmin: record.is_superadmin,
      revision: record.revision,
    };
  else if (!accounts && legacy) view = { ...legacy, revision: null };
  return { accounts, programme, view, isPending: source.isPending, error: source.error };
}

/**
 * The account name, saved under the revision the page last read. The server requires an
 * idempotency key; this one is kept until a save succeeds, so a retry after a lost response replays
 * the first answer instead of failing as a conflict.
 */
export const usePatchAccount = () => {
  const qc = useQueryClient();
  return useKeyedMutation(
    (body: { user_id: string; name: string; revision: string }, opts: CallOptions) =>
      api.patch('/api/v1/account', body, AccountRecord, opts),
    {
      onSuccess: async () => {
        await Promise.all([
          qc.invalidateQueries({ queryKey: keys.account }),
          qc.invalidateQueries({ queryKey: keys.me }),
          qc.invalidateQueries({ queryKey: keys.session }),
        ]);
      },
    },
  );
};

/** Also called directly during page exit, before an asynchronous mutation queue can be suspended. */
export async function putProgress(itemId: string, userId: string, body: z.infer<typeof ProgressPut>) {
  if (queryClient.getQueryData<z.infer<typeof SessionState>>(keys.session)?.user?.id !== userId)
    throw new Error('The account changed before this position could be saved.');
  const saved = await api.put(`/api/v1/progress/${encodeURIComponent(itemId)}`, body, Progress, {
    keepalive: true,
  });
  if (
    saved.position_s !== body.position_s ||
    saved.chapter_index !== body.chapter_index ||
    (body.status === 'completed' && saved.status !== 'completed')
  )
    throw new Error('The server did not confirm this position. Save again.');
  void queryClient.invalidateQueries({ queryKey: keys.item(itemId) });
  void queryClient.invalidateQueries({ queryKey: keys.library });
  void queryClient.invalidateQueries({ queryKey: keys.me });
  return saved;
}

export const useProgressPut = (itemId: string, userId: string) =>
  useMutation({
    scope: { id: `progress:${userId}:${itemId}` },
    mutationFn: (body: z.infer<typeof ProgressPut>) => putProgress(itemId, userId, body),
  });

export const useCreateAttempt = () =>
  useMutation({
    mutationFn: (body: { item_id: string; kind: 'check' | 'quiz' }) =>
      api.post('/api/v1/attempts', body, AttemptCreated),
  });

export const useCreateRecertAttempt = () =>
  useMutation({ mutationFn: () => api.post('/api/v1/recert/attempts', {}, AttemptCreated) });

export const useRenewalPlan = () =>
  useQuery({ queryKey: keys.renewal, queryFn: () => api.get('/api/v1/recert/update', RenewalPlan) });

export const useSubmitAttempt = () => {
  const qc = useQueryClient();
  return useKeyedMutation(
    (
      vars: {
        attempt_id: string;
        answers: { question_id: string; chosen_index: number }[];
        submissionKey?: string;
      },
      opts,
    ) =>
      api.post(
        `/api/v1/attempts/${encodeURIComponent(vars.attempt_id)}/submit`,
        { answers: vars.answers },
        AttemptResult,
        vars.submissionKey ? { ...opts, idempotencyKey: vars.submissionKey } : opts,
      ),
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: keys.library });
        void qc.invalidateQueries({ queryKey: keys.me });
        void qc.invalidateQueries({ queryKey: keys.cards });
        void qc.invalidateQueries({ queryKey: keys.credentials });
        void qc.invalidateQueries({ queryKey: keys.renewal });
        void qc.invalidateQueries({ queryKey: ['item'] });
        void qc.invalidateQueries({ queryKey: ['attempts'] });
      },
    },
  );
};

export const useSaveCard = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; saved: boolean }) =>
      vars.saved
        ? api.post(`/api/v1/cards/${encodeURIComponent(vars.id)}/save`, undefined, Saved)
        : api.del(`/api/v1/cards/${encodeURIComponent(vars.id)}/save`, Saved),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: keys.cards });
      void qc.invalidateQueries({ queryKey: keys.card(vars.id) });
      void qc.invalidateQueries({ queryKey: keys.me });
    },
  });
};

export const useOpenCard = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/cards/${encodeURIComponent(id)}/open`, undefined),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.cards }),
  });
};

export const useImportSavedCards = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      api.post('/api/v1/cards/import-saved', { ids }, Saved.extend({ saved: z.number() })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.cards }),
  });
};

export const useInvite = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, ...body }: { emails: string[]; role: 'learner' | 'operator_admin'; key: string }) =>
      api.post('/api/v1/sponsor/invites', body, InviteResult, { idempotencyKey: key }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['sponsor'] }),
  });
};

export const useRemoveLearner = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.del(`/api/v1/sponsor/learners/${encodeURIComponent(userId)}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['sponsor'] }),
  });
};

export const usePutBrand = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: z.input<typeof BrandPut>) => api.put('/api/v1/sponsor/brand', body, BrandPutResult),
    onSuccess: (r) => {
      qc.setQueryData(keys.sponsor.brand, r.brand);
      return acceptTenantBrand(qc, r.brand);
    },
  });
};

export const useUploadLogo = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, revision }: { file: File; revision: string | null }) => {
      const form = new FormData();
      form.set('file', file);
      form.set('revision', revision ?? '');
      return api.upload('/api/v1/sponsor/brand/logo', form, Brand);
    },
    onSuccess: (b) => {
      qc.setQueryData(keys.sponsor.brand, b);
      return acceptTenantBrand(qc, b);
    },
  });
};

export const useRemoveLogo = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (revision: string | null) =>
      api.del('/api/v1/sponsor/brand/logo', Brand, { body: { revision } }),
    onSuccess: (b) => {
      qc.setQueryData(keys.sponsor.brand, b);
      return acceptTenantBrand(qc, b);
    },
  });
};

export const usePutSlot = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      key: string;
      value: string | null;
      review: ContentReview | null;
      revision: string | null;
    }) =>
      api.put(
        `/api/v1/sponsor/slots/${encodeURIComponent(vars.key)}`,
        { value: vars.value, review: vars.review, revision: vars.revision },
        Slot,
      ),
    onSuccess: (saved, vars) => {
      qc.setQueryData<z.infer<typeof Slot>[]>(keys.sponsor.slots, (rows) =>
        rows?.map((row) => (row.slot_key === saved.slot_key ? saved : row)),
      );
      void qc.invalidateQueries({ queryKey: keys.sponsor.slots });
      void qc.invalidateQueries({ queryKey: keys.sponsor.slotHistory(vars.key) });
    },
  });
};

export const usePublishSlot = () => {
  const qc = useQueryClient();
  return useKeyedMutation(
    (vars: { key: string; publish: boolean; revision: string | null }, opts) =>
      api.post(
        `/api/v1/sponsor/slots/${encodeURIComponent(vars.key)}/${vars.publish ? 'publish' : 'unpublish'}`,
        { revision: vars.revision },
        Slot,
        opts,
      ),
    {
      onSuccess: (saved, vars) => {
        qc.setQueryData<z.infer<typeof Slot>[]>(keys.sponsor.slots, (rows) =>
          rows?.map((row) => (row.slot_key === saved.slot_key ? saved : row)),
        );
        void qc.invalidateQueries({ queryKey: keys.sponsor.slots });
        void qc.invalidateQueries({ queryKey: keys.sponsor.slotHistory(vars.key) });
      },
    },
  );
};

export const usePutSponsorSettings = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: z.input<typeof SponsorSettings>) =>
      api.put('/api/v1/sponsor/settings', body, SponsorSettings),
    onSuccess: async (s) => {
      qc.setQueryData(keys.sponsor.settings, s);
      await qc.cancelQueries({ queryKey: tenantQueryKey });
      qc.setQueryData<TenantPublic>(
        tenantQueryKey,
        (tenant) => tenant && { ...tenant, support_email: s.support_email, enrolment_mode: s.enrolment_mode },
      );
      return refreshTenantConfig(qc);
    },
  });
};

export const useCreateTenant = () => {
  const qc = useQueryClient();
  return useKeyedMutation(
    (body: z.input<typeof TenantCreate>, opts) => api.post('/api/v1/admin/tenants', body, TenantAdmin, opts),
    { onSuccess: () => void qc.invalidateQueries({ queryKey: keys.admin.tenants }) },
  );
};

export const usePatchTenant = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: z.input<typeof TenantPatch> }) =>
      api.patch(`/api/v1/admin/tenants/${encodeURIComponent(vars.id)}`, vars.body, TenantAdmin),
    onSuccess: (saved) => {
      qc.setQueryData<z.infer<typeof TenantAdmin>[]>(keys.admin.tenants, (rows) =>
        rows?.map((row) => (row.id === saved.id ? saved : row)),
      );
      return refreshTenantConfig(qc);
    },
  });
};

export const useRevokeCredential = () => {
  const qc = useQueryClient();
  return useKeyedMutation(
    (vars: { code: string; reason: string }, opts) =>
      api.post(
        `/api/v1/admin/credentials/${encodeURIComponent(vars.code)}/revoke`,
        { reason: vars.reason },
        RegisterRow,
        opts,
      ),
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'register'] }) },
  );
};

export const useAudioState = (id: string) =>
  useQuery({
    queryKey: [...keys.admin.content, id, 'audio'],
    queryFn: () => api.get(`/api/v1/admin/content/${encodeURIComponent(id)}/audio-state`, AudioState),
  });

export const useUploadAudio = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; file: File; duration: number; revision: string | null }) => {
      const form = new FormData();
      form.set('file', vars.file);
      form.set('duration_s', String(vars.duration));
      form.set('revision', vars.revision ?? '');
      return api.upload(`/api/v1/admin/content/${encodeURIComponent(vars.id)}/audio`, form, Saved);
    },
    onSuccess: async (_result, vars) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: keys.admin.content }),
        qc.invalidateQueries({ queryKey: keys.item(vars.id) }),
        qc.invalidateQueries({ queryKey: keys.library }),
      ]);
    },
  });
};

export const useUndoAudio = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; revision: string | null }) =>
      api.post(
        `/api/v1/admin/content/${encodeURIComponent(vars.id)}/audio/undo`,
        { revision: vars.revision },
        Saved,
        { idempotencyKey: uuidv7() },
      ),
    onSuccess: async (_result, vars) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: keys.admin.content }),
        qc.invalidateQueries({ queryKey: keys.item(vars.id) }),
        qc.invalidateQueries({ queryKey: keys.library }),
      ]);
    },
  });
};

export const usePatchAdminUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { user_id: string; is_superadmin: boolean }) =>
      api.patch('/api/v1/admin/users', body, AdminUser),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
};
